import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  getBankDetails,
  getProfile,
  getReviews,
  getVerificationStatus,
  updateOperatingMode,
} from "../../lib/api/contractor";
import { ApiError } from "../../lib/api/client";
import { useAuth } from "../../lib/auth";
import { dateOnly, money } from "../../lib/format";
import {
  jobCategoryDisplay,
  parseBankVerificationStatus,
  parseOperatingMode,
  verificationBadge,
  type OperatingMode,
} from "../../lib/types/enums";
import type {
  AnonymizedReview,
  BankDetails,
  Contractor,
  ModeSwitchBlocker,
  VerificationStatusResponse,
} from "../../lib/types/models";
import {
  Avatar,
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  RadioGroup,
  Skeleton,
  SkeletonList,
  SpecList,
  StatusPill,
  useToast,
} from "@ui/react";
import { ShieldIcon } from "@ui/react/icons";
import { errorMessage } from "../../lib/errorMessage";

interface ProfileData {
  profile: Contractor;
  reviews: AnonymizedReview[];
  /** Null when the verification call failed; the badge then falls back to the
   *  profile document, which carries the same two fields. */
  verification: VerificationStatusResponse | null;
  /** Null when the payout read failed — the card is then left off rather than
   *  claiming there is no account on file. */
  bank: BankDetails | null;
}

/* ----------------------------------------------------------- verification */

interface BadgeCopy {
  /** Absent for the verified state: the pill beside the heading already says
   *  "Verified", and a banner repeating it 40px lower adds nothing. */
  title?: string;
  tone: "info" | "warn" | "danger";
  body: string;
}

/** Three explicit states. Each says what the contractor can and cannot do right
 *  now — "Pending" on its own tells them nothing actionable. */
function badgeCopy(badge: "verified" | "rejected" | "pending"): BadgeCopy {
  if (badge === "verified") {
    return {
      tone: "info",
      body: "Your account is active. You can browse jobs, send quotes and take on work.",
    };
  }
  if (badge === "rejected") {
    return {
      title: "Verification declined",
      tone: "danger",
      body:
        "Your account was not approved, so you cannot browse or quote on jobs. " +
        "Contact Shoku support to find out what is needed and to reapply.",
    };
  }
  return {
    title: "Pending review",
    tone: "warn",
    body:
      "Someone at Shoku is still reviewing your account. You can finish your profile " +
      "and bank details now, but job browsing and quoting stay locked until you're verified.",
  };
}

/* ------------------------------------------------------------ mode switch */

/** The 409 body is `{ from, to, blockers: [...] }`, which ApiErrorBody does not
 *  describe. Reading it defensively keeps a shape change from throwing inside
 *  the error path, where a second failure would leave no message at all. */
function blockersFrom(error: unknown): ModeSwitchBlocker[] {
  if (!(error instanceof ApiError)) return [];
  const raw = (error.body as { blockers?: unknown } | null)?.blockers;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is ModeSwitchBlocker => {
    return Boolean(item) && typeof item === "object" && "hint" in (item as object);
  });
}

const MODE_COPY: Record<OperatingMode, { label: string; blurb: string }> = {
  marketplace: {
    label: "Marketplace",
    blurb:
      "You browse public jobs and send quotes. Homeowners choose you from the quotes they receive.",
  },
  bookable: {
    label: "Direct booking",
    blurb:
      "Customers book slots straight off your calendar at your listed prices. Marketplace jobs are hidden.",
  },
};

/** Where each blocker is actually fixed.
 *
 *  The two setup blockers are fixed on screens a marketplace contractor has no
 *  navigation entry for (only the bookable shell shows "Booking setup"), so the
 *  dialog has to carry the way there itself.
 *
 *  `hint` overrides the server's wording, and `activeJob` is the only kind that
 *  uses it: "Complete or cancel these jobs first" named an action — cancelling —
 *  that a contractor has never been able to take, so it read as a missing
 *  button next to two blockers that were real links. Withdrawing is what they
 *  can do, and Active work is where the button is. */
const BLOCKER_LINKS: Record<string, { href: string; label: string; hint?: string }> = {
  noEnabledService: { href: "/settings/services", label: "Add a service" },
  noWeeklyAvailability: { href: "/settings/availability", label: "Set your hours" },
  activeJob: {
    href: "/active",
    label: "Active work",
    hint: "Finish these jobs, or withdraw from them",
  },
};

/* ------------------------------------------------------------------ screen */

export function ProfileScreen() {
  const { setContractor } = useAuth();
  const toast = useToast();

  const [state, setState] = useState<{
    loading: boolean;
    error: unknown;
    data: ProfileData | null;
  }>({ loading: true, error: null, data: null });

  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, loading: true, error: null }));

    // allSettled, not all: the reviews, verification and payout reads are all
    // decoration around the profile. Letting any of them blank the whole screen
    // would hide the contractor's own details for no reason.
    const [profileResult, reviewsResult, verificationResult, bankResult] = await Promise.allSettled([
      getProfile(),
      getReviews(),
      getVerificationStatus(),
      getBankDetails(),
    ]);

    if (seq !== requestSeq.current) return;

    if (profileResult.status === "rejected") {
      setState({ loading: false, error: profileResult.reason, data: null });
      return;
    }

    setState({
      loading: false,
      error: null,
      data: {
        profile: profileResult.value,
        reviews: reviewsResult.status === "fulfilled" ? reviewsResult.value.reviews ?? [] : [],
        verification: verificationResult.status === "fulfilled" ? verificationResult.value : null,
        bank: bankResult.status === "fulfilled" ? bankResult.value : null,
      },
    });
    setContractor(profileResult.value);
  }, [setContractor]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Verification is an offline admin action with no notification of any kind —
   * no push, no email, nothing the client can subscribe to. A tab left open
   * would sit on "Pending review" forever, so the status is re-read whenever the
   * contractor comes back to the tab. Only the cheap status call is repeated.
   */
  useEffect(() => {
    const onFocus = () => {
      void (async () => {
        try {
          const verification = await getVerificationStatus();
          setState((s) => (s.data ? { ...s, data: { ...s.data, verification } } : s));
        } catch {
          // A failed refresh keeps the last known status; the visible state is
          // still true as of the last successful read.
        }
      })();
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);


  const [pendingMode, setPendingMode] = useState<OperatingMode | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<unknown>(null);
  const [blockers, setBlockers] = useState<ModeSwitchBlocker[] | null>(null);

  const data = state.data;
  const profile = data?.profile ?? null;

  const confirmSwitch = useCallback(async () => {
    if (!pendingMode) return;
    setSwitching(true);
    setSwitchError(null);
    const switchedTo = pendingMode;
    try {
      await updateOperatingMode(pendingMode);
      setPendingMode(null);
      // The response is the booking settings, not the contractor, so the profile
      // is re-read: the shell's navigation is driven by operatingMode and would
      // otherwise keep showing the half of the app they just left.
      await load();
      toast.show(
        switchedTo === "bookable" ? "You're now on direct booking." : "You're now on marketplace work.",
      );
    } catch (error) {
      const found = blockersFrom(error);
      setPendingMode(null);
      if (found.length > 0) setBlockers(found);
      else setSwitchError(error);
    } finally {
      setSwitching(false);
    }
  }, [load, pendingMode, toast]);

  if (state.loading && !data) {
    return (
      <>
        <PageHead title="Profile" />
        <div className="stack" role="status" aria-label="Loading your profile">
          <Skeleton height="10rem" />
          <Skeleton height="6rem" />
          <SkeletonList rows={3} />
        </div>
      </>
    );
  }

  if (state.error && !data) {
    return (
      <>
        <PageHead title="Profile" />
        <ErrorState message={errorMessage(state.error)} onRetry={() => void load()} />
      </>
    );
  }

  if (!data || !profile) {
    return (
      <>
        <PageHead title="Profile" />
        <EmptyState title="No profile yet">
          We couldn't find your contractor profile. Try loading it again.
        </EmptyState>
      </>
    );
  }

  const verification = data.verification;
  const badge = verificationBadge({
    isVerified: verification?.isVerified ?? profile.isVerified,
    status: verification?.status ?? profile.status,
  });
  const copy = badgeCopy(badge);
  const blockedFromNewWork = verification?.blockedFromNewWork ?? false;
  const mode = parseOperatingMode(profile.operatingMode);
  // "none" has no label in the shared status table, and an account Paystack has
  // actually created always carries one of the other three.
  const bankStatus = data.bank
    ? parseBankVerificationStatus(data.bank.bankVerificationStatus)
    : "none";
  const fullName = `${profile.firstName} ${profile.lastName}`.trim();

  return (
    <>
      {/* Sign out is NOT here any more. It lived in two places with two
          behaviours — instant in the old sidebar, confirmed here — because the
          sidebar vanished on a phone. The shell's account menu is the one
          control now, at every width. */}
      <PageHead
        title="Profile"
        actions={
          <Link className="btn btn-secondary" to="/profile/edit">
            Edit profile
          </Link>
        }
      />

      <div className="stack stack--loose">
        {/* ---- identity ---- */}
        <div className="card card--dense">
          <div className="cluster">
            {/* The halo is the availability signal, per DESIGN.md — so it is
                gated on availability rather than being decoration. */}
            <Avatar name={fullName} size="lg" available={profile.isAvailable} />
            <div className="stack stack--tight">
              <span className="headline-sm">{fullName}</span>
              {profile.businessName ? (
                <span className="label-sm muted">{profile.businessName}</span>
              ) : null}
              <span className="muted">{profile.email}</span>
            </div>
            <div className="stack stack--tight stat-block">
              <span className="amount amount--md">
                {profile.rating > 0 ? profile.rating.toFixed(1) : "—"}
              </span>
              <span className="label-sm muted">
                {profile.rating > 0 ? "Rating out of 5" : "Not rated yet"}
              </span>
              <span className="muted">
                {profile.jobsCompleted} {profile.jobsCompleted === 1 ? "job" : "jobs"} completed
              </span>
            </div>
          </div>
        </div>

        {/* ---- verification ---- */}
        <div className="card card--dense">
          <div className="stack">
            <div className="cluster">
              <ShieldIcon />
              <h2 className="headline-sm">Verification</h2>
              <StatusPill kind="verification" status={badge} />
            </div>
            <Banner tone={copy.tone} title={copy.title}>
              {copy.body}
            </Banner>

            {/* blockedFromNewWork is its own axis: the account is verified, so
                the badge above stays green, but new work is barred. Folding the
                two together would tell the contractor their verification had
                failed, which is not what happened. */}
            {blockedFromNewWork ? (
              <Banner tone="danger" title="Blocked from new work">
                Your account is verified but has been put on hold for new work. You can finish
                jobs you already have, but you cannot pick up or quote on anything new. Contact
                Shoku support to sort this out.
              </Banner>
            ) : null}
          </div>
        </div>

        {/* ---- operating mode ---- */}
        <div className="card card--dense">
          <div className="stack">
            <h2 className="headline-sm">How you take work</h2>
            <p className="muted">{MODE_COPY[mode].blurb}</p>
            {/* One of two, never both: a radio group, not two independent
                toggles that a screen reader announces as unrelated switches.
                `value` stays on the CURRENT mode, so choosing the other one
                opens the confirmation rather than switching underneath you. */}
            <RadioGroup
              legend="Operating mode"
              name="operating-mode"
              value={mode}
              options={(["marketplace", "bookable"] as const).map((option) => ({
                value: option,
                label: MODE_COPY[option].label,
              }))}
              onChange={(option) => {
                if (mode !== option) setPendingMode(option);
              }}
            />
            <p className="dim">
              Switching changes which half of Shoku you use: marketplace mode shows the job feed
              and quotes, direct booking replaces both with a bookings inbox and your calendar.
            </p>
            {/* The marketplace shell has no "Booking setup" tab, and the switch
                refuses until a service and weekly hours exist — without this
                link nobody could ever get there. */}
            <div>
              <Link className="btn btn-secondary" to="/settings/booking">
                {mode === "bookable" ? "Booking setup" : "Set up direct booking"}
              </Link>
            </div>
            {switchError ? (
              <Banner tone="danger" title="Couldn't switch mode">
                {errorMessage(switchError)}
              </Banner>
            ) : null}
          </div>
        </div>

        {/* ---- details ---- */}
        <div className="card card--dense">
          <div className="stack">
            <h2 className="headline-sm">Your details</h2>

            <SpecList
              items={[
                {
                  label: "Hourly rate",
                  value: profile.hourlyRate
                    ? `${money(profile.hourlyRate, profile.currency)} per hour`
                    : "Not set",
                },
                {
                  label: "About you",
                  value: profile.bio ? (
                    profile.bio
                  ) : (
                    <span className="muted">
                      No bio yet — homeowners see this before choosing a quote.
                    </span>
                  ),
                },
                {
                  label: "Trades",
                  value:
                    profile.skills.length > 0 ? (
                      <span className="chip-row">
                        {profile.skills.map((skill) => (
                          <span className="chip" key={skill}>
                            {/* Skills are stored as the raw enum value
                                ("general"), which is not what a contractor
                                calls their trade. */}
                            {jobCategoryDisplay(skill)}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="muted">No trades listed.</span>
                    ),
                },
                {
                  label: "Service areas",
                  value:
                    profile.serviceAreaGeos.length > 0 ? (
                      <span className="chip-row">
                        {profile.serviceAreaGeos.map((area) => (
                          <span
                            className="chip"
                            key={`${area.name}-${area.latitude}-${area.longitude}`}
                          >
                            {area.name}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="muted">No service areas listed.</span>
                    ),
                },
                {
                  label: "Availability",
                  value: profile.isAvailable
                    ? "Available — you appear in searches and can take on work."
                    : "Unavailable — you are hidden from new work until you switch this back on.",
                },
              ]}
            />
          </div>
        </div>

        {/* ---- payouts ----
            Read-only on purpose. POST /profile/bank-details creates a Paystack
            subaccount and RETURNS THE EXISTING ONE once there is one, so an
            edit form here would look saved and change nothing — see
            BankDetailsScreen, which is gated on `needsBankDetails` and is
            therefore unreachable the moment that gate passes. Until there is an
            update path, support is the honest answer. */}
        {data.bank ? (
          <div className="card card--dense">
            <div className="stack">
              <div className="cluster">
                <h2 className="headline-sm">Payouts</h2>
                {bankStatus === "none" ? null : <StatusPill kind="bank" status={bankStatus} />}
              </div>
              <SpecList
                items={[
                  { label: "Bank", value: data.bank.bankName ?? "Not recorded" },
                  { label: "Account name", value: data.bank.accountName ?? "Not recorded" },
                  {
                    label: "Account number",
                    value: data.bank.accountNumberMasked ?? "Not recorded",
                  },
                ]}
              />
              <p className="dim">
                Money for every signed-off job is paid into this account. To change these
                details, email shoku-support@businessblacksmiths.com.
              </p>
            </div>
          </div>
        ) : null}

        {/* ---- reviews ---- */}
        <div className="stack">
          <h2 className="headline-sm">
            Reviews{data.reviews.length > 0 ? ` (${data.reviews.length})` : ""}
          </h2>
          <p className="dim">
            Reviews are anonymous by design — you see the job and the rating, never who wrote it.
          </p>
          {data.reviews.length === 0 ? (
            <EmptyState title="No reviews yet">
              Once homeowners sign off on your finished jobs, their reviews appear here.
            </EmptyState>
          ) : (
            // Reviews are the one list here nothing can be done with, so they
            // are told apart by generous space rather than by a hover lift that
            // would promise an interaction that does not exist.
            <div className="stack stack--loose">
              {data.reviews.map((review) => (
                <div key={`${review.jobId}-${review.createdAt}`} className="card card--dense">
                  <div className="stack stack--tight">
                    <div className="row__top">
                      <span className="row__title">Job #{review.jobId.slice(0, 8)}</span>
                      <span className="muted">{dateOnly(review.createdAt)}</span>
                    </div>
                    <span>
                      <span className="amount">{review.rating.toFixed(1)}</span>{" "}
                      <span className="muted">out of 5</span>
                    </span>
                    {review.comment ? <p>{review.comment}</p> : <p className="muted">No comment left.</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {pendingMode ? (
        <ConfirmDialog
          title={`Switch to ${MODE_COPY[pendingMode].label.toLowerCase()}?`}
          confirmLabel={`Switch to ${MODE_COPY[pendingMode].label.toLowerCase()}`}
          confirming={switching}
          onCancel={() => setPendingMode(null)}
          onConfirm={() => void confirmSwitch()}
          body={
            <>
              <p>{MODE_COPY[pendingMode].blurb}</p>
              <p>
                This changes which half of Shoku you use. You can only be in one mode at a time,
                and everything belonging to the other mode disappears from your navigation until
                you switch back.
              </p>
            </>
          }
        />
      ) : null}

      {blockers ? (
        <Modal
          title="You can't switch modes yet"
          onClose={() => setBlockers(null)}
          actions={
            <Button variant="primary" onClick={() => setBlockers(null)}>
              Got it
            </Button>
          }
        >
          <div className="stack">
            <p className="muted">Shoku needs these sorted out first:</p>
            {/* The server's own words, except where BLOCKER_LINKS overrides
                them: the server knows exactly what is in the way, and
                paraphrasing it into "couldn't switch" would leave the
                contractor with nothing to act on. */}
            {/* The shared reset strips list markers globally, so the bullets are
                restored inline — a bulleted list is the point of this dialog. */}
            <ul className="stack stack--tight bullet-list">
              {blockers.map((blocker, index) => {
                const fix = BLOCKER_LINKS[blocker.kind];
                return (
                  <li key={`${blocker.kind}-${index}`}>
                    {fix?.hint ?? blocker.hint}
                    {blocker.count > 0 ? <span className="muted"> ({blocker.count})</span> : null}
                    {fix ? (
                      <>
                        {" — "}
                        <Link className="link-inline" to={fix.href}>{fix.label}</Link>
                      </>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </Modal>
      ) : null}

    </>
  );
}
